import React, { useState, useEffect, useRef } from "react";

const VideoTutorial = ({ videoId, onClose }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const playerInitialized = useRef(false);

  useEffect(() => {
    // Load YouTube IFrame API
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Initialize player when API is ready
    const initPlayer = () => {
      if (window.YT && window.YT.Player && !playerInitialized.current) {
        playerInitialized.current = true;
        playerRef.current = new window.YT.Player("youtube-player", {
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            modestbranding: 1,
            rel: 0
          },
          events: {
            onReady: (event) => {
              // Restore saved time if available
              const savedTime = sessionStorage.getItem("tutorialVideoTime");
              if (savedTime) {
                event.target.seekTo(parseFloat(savedTime));
              }
            }
          }
        });

        // Save current time periodically
        intervalRef.current = setInterval(() => {
          if (
            playerRef.current &&
            typeof playerRef.current.getCurrentTime === "function"
          ) {
            const time = playerRef.current.getCurrentTime();
            sessionStorage.setItem("tutorialVideoTime", time.toString());
          }
        }, 1000);
      }
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (
        playerRef.current &&
        typeof playerRef.current.getCurrentTime === "function"
      ) {
        const time = playerRef.current.getCurrentTime();
        sessionStorage.setItem("tutorialVideoTime", time.toString());
      }
    };
  }, [videoId]);

  const handleClose = () => {
    // Save current time before closing
    if (
      playerRef.current &&
      typeof playerRef.current.getCurrentTime === "function"
    ) {
      const time = playerRef.current.getCurrentTime();
      sessionStorage.setItem("tutorialVideoTime", time.toString());
    }
    sessionStorage.setItem("tutorialVideoClosed", "true");
    if (onClose) {
      onClose();
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex flex-col rounded-lg bg-white shadow-2xl transition-all ${
        isMinimized ? "h-14 w-80" : "h-96 w-168.7"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-lg bg-green-12 px-4 py-2">
        <h3 className="font-InterBold text-ft14-20 text-white">
          {isMinimized ? "Tutorial Video (Click to expand)" : "Tutorial Video"}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMinimize}
            className="text-white transition-opacity hover:opacity-80"
            aria-label={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? (
              <svg
                className="size-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            ) : (
              <svg
                className="size-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            )}
          </button>
          <button
            onClick={handleClose}
            className="text-white transition-opacity hover:opacity-80"
            aria-label="Close"
          >
            <svg
              className="size-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Video Content */}
      <div className={`flex-1 p-2 ${isMinimized ? "hidden" : ""}`}>
        <div id="youtube-player" className="size-full"></div>
      </div>
    </div>
  );
};

export default VideoTutorial;
